/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import PrescriptionTemplateForm from "components/forms/PrescriptionTemplateForm";
import Button from "components/generals/Button";
import DefaultCell from "components/generals/DefaultCell";
import { Modal } from "components/generals/Modal";
import useHttp from "hooks/http.hook";
import { CoreAPI_URLs } from "constants/http.const";
import DataTable from "dashboard-template/examples/Tables/DataTable";
import { TableProps } from "interfaces";
import { HeaderEnum, TemplateInterface, TemplateTypeEnum } from "medicalsystem-shared";
import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { EntityPermissions, FormActionType, PermissionsUser } from "types";
import { getNewPrescriptionTemplateDataContent } from "utils/model.utils";
import Box from "components/generals/Box";
import ButtonDialog from "components/generals/ButtonDialog";
import PermissionWrapper from "components/generals/PermissionWrapper";
import { TagsInterface } from "interfaces";

/**
 * Extended properties for the PrescriptionTemplateTable component.
 * 
 * @interface PrescriptionTemplateTableProps
 * @extends {TableProps<TemplateInterface>}
 * @property {TemplateInterface} [selectedRow] - Currently selected template row
 * @property {string} [canSearchPlaceholder] - Custom placeholder for search input
 * @property {boolean} [apiPagination] - Enable API-based pagination
 * @property {number} [apiPageTotal] - Total number of pages from API
 * @property {number} [apiRowsTotal] - Total number of rows from API
 * @property {number} [apiPageCurrentIndex] - Current page index from API
 * @property {string} [customTag] - Custom tag filter
 * @property {TagsInterface[]} [filterTag] - Array of tags to filter by
 */
interface PrescriptionTemplateTableProps extends TableProps<TemplateInterface> {
  selectedRow?: TemplateInterface;
  canSearchPlaceholder?: string;
  apiPagination?: boolean;
  apiPageTotal?: number;
  apiRowsTotal?: number;
  apiPageCurrentIndex?: number;
  customTag?: string;
  filterTag?: TagsInterface[];
}

/**
 * Prescription template table component for displaying and managing prescription templates.
 * 
 * Features:
 * - Displays prescription template information in sortable data table
 * - Columns: title, version, tags, action buttons
 * - View/edit template functionality via modal with PrescriptionTemplateForm
 * - Delete template with confirmation dialog and list update
 * - Optional select function for template selection workflow
 * - Read-only mode support
 * - Tags display with array-to-string conversion
 * - Permission-based access control
 * - API-based or client-side pagination support
 * - Custom search placeholder
 * - Tag filtering support
 * - Selected row highlighting
 * 
 * Template Content:
 * - Medication name and dosage
 * - Administration instructions
 * - Frequency and duration
 * - Refill information
 * - Special instructions
 * - Drug interaction warnings
 * 
 * Security:
 * - Uses HeaderEnum.RESOURCE_ID for secure identification
 * - Permission-based rendering via PermissionWrapper
 * - Validates entity permissions before operations
 * 
 * @param {PrescriptionTemplateTableProps} props - Table properties
 * @returns {JSX.Element} Prescription template table with optional API pagination
 */
const PrescriptionTemplateTable: FC<PrescriptionTemplateTableProps> = ({
  modelList,
  setModelList,
  selectFunction,
  readOnlyValue,
  selectedRow,
  customTag,
  canSearchPlaceholder,
  apiPagination,
  apiPageTotal,
  apiRowsTotal,
  apiPageCurrentIndex,
  filterTag,
  ...props
}) => {
  const [selectedPrescriptionTemplate, setSelectedPrescriptionTemplate] =
    useState<TemplateInterface>(getNewPrescriptionTemplateDataContent());
  const [activeEditPrescriptionTemplateModal, setActiveEditPrescriptionTemplateModal] =
    useState<boolean>(false);
  const [modalAction, setModalAction] = useState<FormActionType>();
  const { t } = useTranslation();
  const prescriptionTemplates = useMemo(() => {
    return modelList;
  }, [modelList]);

  const getUITable = (list: TemplateInterface[]) => {
    return {
      columns: [
        {
          Header: t("prescriptiontemplateTable.title"),
          accessor: "title",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
        {
          Header: t("prescriptiontemplateTable.version"),
          accessor: "version",
          sortIndicatorType: "numeric",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
        {
          Header: t("prescriptiontemplateTable.tags"),
          accessor: "tags",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => {
            const displayValue = Array.isArray(value) ? value.join(", ") : value;
            return <DefaultCell value={displayValue} />;
          },
        },
        {
          Header: t("prescriptiontemplateTable.action"),
          accessor: "id",
          disableSortBy: true,
          Cell: ({ value }: any) => (
            <>
              <Button
                id="cy-view-prescription"
                sx={{ marginRight: 2 }}
                variant="contained"
                color="info"
                onClick={() => {
                  const prescriptionTemplate = list.find((p) => p.id === value);
                  if (prescriptionTemplate) {
                    setModalAction(FormActionType.MODIFY);
                    openModalHandler(prescriptionTemplate);
                  }
                }}
              >
                {t("prescriptiontemplateTable.view")}
              </Button>

              <Button
                id="cy-select-prescription"
                sx={{ marginRight: 2 }}
                variant="contained"
                color="info"
                onClick={() => {
                  const prescriptionTemplate = list.find((p) => p.id === value);
                  if (prescriptionTemplate) {
                    selectFunction && selectFunction(prescriptionTemplate);
                  }
                }}
              >
                {t("prescriptiontemplateTable.select")}
              </Button>

              {!readOnlyValue ? (
                <PermissionWrapper
                  requiredPermissions={[PermissionsUser[EntityPermissions.PRESCRIPTIONS].DELETE]}
                >
                  <ButtonDialog
                    onAcceptHandle={() => {
                      const prescriptionTemplate = list.find((p) => p.id === value);
                      if (prescriptionTemplate) {
                        deleteHandler(prescriptionTemplate);
                      }
                    }}
                    buttonText={t("prescriptiontemplateTable.deleteAction")}
                    title={t("prescriptiontemplateTable.deleteConfirmation")}
                    description={t("prescriptiontemplateTable.description")}
                    labelledbyID={"prescriptionTemplateDelete"}
                    describedbyID={"prescriptionTemplateDescriptionId"}
                  />
                </PermissionWrapper>
              ) : null}
            </>
          ),
        },
      ],

      rows: list,
    };
  };

  const dataTableData = useMemo(() => getUITable(prescriptionTemplates), [modelList]);
  const [_isLoading, _error, sendRequest] = useHttp();
  const openModalHandler = (prescriptionTemplate: TemplateInterface) => {
    if (customTag) {
      prescriptionTemplate.tags.push(customTag);
    }
    setSelectedPrescriptionTemplate(prescriptionTemplate);
    setActiveEditPrescriptionTemplateModal(true);
  };

  const deleteHandler = (prescriptionTemplate: TemplateInterface) => {
    setSelectedPrescriptionTemplate(prescriptionTemplate);

    sendRequest<boolean, TemplateInterface>(
      CoreAPI_URLs.PRESCRIPTION_TEMPLATE_GENERAL_OPERATIONS,
      onPrescriptionTemplateDeleteHandler,
      "DELETE",
      JSON.stringify(prescriptionTemplate),
      [{ header: HeaderEnum.RESOURCE_ID, value: prescriptionTemplate.id }],
      { payload: prescriptionTemplate }
    );
  };

  const onPrescriptionTemplateDeleteHandler = (
    result: boolean,
    prescriptionTemplate: TemplateInterface
  ) => {
    const indexList = modelList.indexOf(prescriptionTemplate);
    modelList.splice(indexList, 1);
    setModelList([...modelList]);
  };

  return (
    <>
      <Box mt={2} mb={2} width="100%" display="flex" justifyContent="flex-start">
        <PermissionWrapper
          requiredPermissions={[PermissionsUser[EntityPermissions.PRESCRIPTIONS].VIEW]}
        >
          <Button
            type="button"
            variant="contained"
            color="primary"
            onClick={() => {
              var newTemplate = getNewPrescriptionTemplateDataContent();
              newTemplate.tags.push(customTag);
              setModalAction(FormActionType.NEW);
              setSelectedPrescriptionTemplate(newTemplate);
              setActiveEditPrescriptionTemplateModal(true);
            }}
          >
            {t("prescriptiontemplatesDataContent.newPrescriptionTemplate")}
          </Button>
        </PermissionWrapper>
      </Box>
      <DataTable {...props} table={dataTableData} canSearch  canSearchPlaceholder={t("billingEncounterDataContent.search")}/>

      <Modal
        open={activeEditPrescriptionTemplateModal}
        onClose={() => {
          setActiveEditPrescriptionTemplateModal(false);
        }}
      >
        <PrescriptionTemplateForm
          readOnlyValue={true}
          modelList={modelList}
          setModelList={setModelList}
          action={modalAction}
          model={selectedPrescriptionTemplate}
          onCloseAction={() => {
            setActiveEditPrescriptionTemplateModal(false);
          }}
          isModal={true}
          filterTag={filterTag}
        />
      </Modal>
    </>
  );
};

export default PrescriptionTemplateTable;
